import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, useMediaQuery, useTheme, type Theme } from "@mui/material";
import { useCreatePath, useStore } from "react-admin";
import { useNavigate } from "react-router-dom";
import { isAsharaEvent } from "@/utils";
import type {
  CurrentEvent,
  EventStatsAttendanceSlice,
  EventStatsNiyaazRow,
} from "@/containers/events/types";
import { EventStatsMobileGrid, type EventStatField } from "./EventStatsMobileGrid";

type EventDashboardMarkazStatsProps = {
  niyaazCounts: EventStatsNiyaazRow[];
  selectedMarkaz: string;
};

const formatInr = (amount: number) => `₹ ${Intl.NumberFormat("en-IN").format(amount)}`;

const emptyAttendanceSlice = (): EventStatsAttendanceSlice => ({
  gentsCount: 0,
  ladiesCount: 0,
  childrenUnder10: 0,
  totalCount: 0,
});

const resolveAttendance = (row: EventStatsNiyaazRow) => ({
  physical: row.physical ?? emptyAttendanceSlice(),
  relay: row.relay ?? emptyAttendanceSlice(),
});

const headerCellSx = {
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

type AttendanceGroup = "physical" | "relay" | "total";

const attendanceGroupPalette: Record<AttendanceGroup, "primary" | "secondary" | "info"> = {
  physical: "primary",
  relay: "secondary",
  total: "info",
};

type GroupSurfaceRole = "title" | "subheader" | "body";

const groupFillOpacity = (theme: Theme, role: GroupSurfaceRole) => {
  if (role === "body") {
    return 0;
  }
  if (theme.palette.mode === "dark") {
    return role === "title" ? 0.14 : 0.09;
  }
  return role === "title" ? 0.08 : 0.05;
};

const groupEdgeAlpha = (theme: Theme) => (theme.palette.mode === "dark" ? 0.38 : 0.26);

const groupEdgeBorderSx = (
  theme: Theme,
  group: AttendanceGroup,
  edges: { start?: boolean; end?: boolean }
) => {
  const main = theme.palette[attendanceGroupPalette[group]].main;
  const color = alpha(main, groupEdgeAlpha(theme));
  const width = 2;
  const shadows: string[] = [];

  if (edges.start) {
    shadows.push(`inset ${width}px 0 0 0 ${color}`);
  }
  if (edges.end) {
    shadows.push(`inset -${width}px 0 0 0 ${color}`);
  }

  return shadows.length > 0 ? { boxShadow: shadows.join(", ") } : {};
};

const groupSurfaceSx = (
  theme: Theme,
  group: AttendanceGroup,
  role: GroupSurfaceRole,
  options?: { edge?: "start" | "end" | "both" }
) => {
  const paletteKey = attendanceGroupPalette[group];
  const main = theme.palette[paletteKey].main;
  const fillOpacity = groupFillOpacity(theme, role);
  const edge =
    options?.edge === "both"
      ? { start: true, end: true }
      : options?.edge === "start"
        ? { start: true, end: false }
        : options?.edge === "end"
          ? { start: false, end: true }
          : { start: false, end: false };

  return {
    ...(fillOpacity > 0 && { bgcolor: alpha(main, fillOpacity) }),
    ...groupEdgeBorderSx(theme, group, edge),
  };
};

const groupHeaderSx = (theme: Theme, group: AttendanceGroup) => ({
  fontWeight: 600,
  color: "text.primary",
  ...groupSurfaceSx(theme, group, "title", { edge: "start" }),
});

const AttendanceSummaryStrip = ({ row }: { row: EventStatsNiyaazRow }) => {
  const { physical, relay } = resolveAttendance(row);

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, px: 0.5, lineHeight: 1.6 }}>
      <Typography component="span" variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {row.totalCount}
      </Typography>{" "}
      registered ·{" "}
      <Typography component="span" variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {physical.totalCount}
      </Typography>{" "}
      on-site ·{" "}
      <Typography component="span" variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {relay.totalCount}
      </Typography>{" "}
      relay ·{" "}
      <Typography component="span" variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {row.count}
      </Typography>{" "}
      forms
      {row.relayRegistrations > 0 ? (
        <>
          {" "}
          (
          <Typography
            component="span"
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {row.relayRegistrations}
          </Typography>{" "}
          with relay)
        </>
      ) : null}
    </Typography>
  );
};

const AttendanceCells = ({
  slice,
  showChildren = true,
  group,
}: {
  slice: EventStatsAttendanceSlice;
  showChildren?: boolean;
  group: AttendanceGroup;
}) => {
  const theme = useTheme();

  return (
    <>
      <TableCell align="center" sx={groupSurfaceSx(theme, group, "body", { edge: "start" })}>
        {slice.gentsCount}
      </TableCell>
      <TableCell align="center" sx={groupSurfaceSx(theme, group, "body")}>
        {slice.ladiesCount}
      </TableCell>
      {showChildren ? (
        <TableCell align="center" sx={groupSurfaceSx(theme, group, "body")}>
          {slice.childrenUnder10}
        </TableCell>
      ) : null}
      <TableCell
        align="center"
        sx={{
          ...groupSurfaceSx(theme, group, "body"),
          fontWeight: 600,
        }}
      >
        {slice.totalCount}
      </TableCell>
    </>
  );
};

const GroupSubHeaderCells = ({
  group,
  showChildren = true,
}: {
  group: AttendanceGroup;
  showChildren?: boolean;
}) => {
  const theme = useTheme();

  return (
    <>
      <TableCell
        align="center"
        sx={{ ...headerCellSx, ...groupSurfaceSx(theme, group, "subheader", { edge: "start" }) }}
      >
        Gents
      </TableCell>
      <TableCell
        align="center"
        sx={{ ...headerCellSx, ...groupSurfaceSx(theme, group, "subheader") }}
      >
        Ladies
      </TableCell>
      {showChildren ? (
        <TableCell
          align="center"
          sx={{ ...headerCellSx, ...groupSurfaceSx(theme, group, "subheader") }}
        >
          Children (&lt;10)
        </TableCell>
      ) : null}
      <TableCell
        align="center"
        sx={{ ...headerCellSx, ...groupSurfaceSx(theme, group, "subheader") }}
      >
        Total
      </TableCell>
    </>
  );
};

export const EventDashboardMarkazStats = ({
  niyaazCounts,
  selectedMarkaz,
}: EventDashboardMarkazStatsProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent");
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const isAshara = isAsharaEvent(currentEvent ?? undefined);

  const goToNiyaazWithZabihats = () => {
    const listPath = createPath({ type: "list", resource: "niyaaz" });
    const filter = { zabihat_gt: 0 };
    const search = `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    navigate(`${listPath}${search}`);
  };

  const goToNiyaazWithRelay = () => {
    const listPath = createPath({ type: "list", resource: "niyaaz" });
    const filter = { relayRequired: true };
    const search = `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    navigate(`${listPath}${search}`);
  };

  const goToNiyaazWithPirsa = () => {
    const listPath = createPath({ type: "list", resource: "niyaaz" });
    const filter = { pirsa_gt: 0 };
    const search = `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    navigate(`${listPath}${search}`);
  };

  const rows = niyaazCounts.filter((r) => r.markaz === selectedMarkaz);

  const buildFinancialFields = (row: EventStatsNiyaazRow): EventStatField[] => {
    const fields: EventStatField[] = [
      {
        label: isAshara ? "Niyaaz Amount" : "Takhmeen Amount",
        value: formatInr(row.takhmeenAmount),
      },
      { label: "Gents Chairs", value: row.gentsChairs ?? 0 },
      { label: "Ladies Chairs", value: row.ladiesChairs ?? 0 },
    ];

    if (!isAshara) {
      fields.push({ label: "Iftaari", value: formatInr(row.iftaari) });
    }

    fields.push({ label: "Zabihats", value: row.zabihat, onClick: goToNiyaazWithZabihats });

    if (isAshara && row.relayRegistrations > 0) {
      fields.push({
        label: "Forms with relay",
        value: row.relayRegistrations,
        onClick: goToNiyaazWithRelay,
      });
    }

    fields.push(
      { label: "Pirsa", value: row.pirsa, onClick: goToNiyaazWithPirsa },
      { label: "Payable", value: formatInr(row.totalPayable) },
      { label: "Paid", value: formatInr(row.paidAmount) },
      { label: "Balance", value: formatInr(row.totalPayable - row.paidAmount) }
    );

    return fields;
  };

  const buildAsharaMobileFields = (row: EventStatsNiyaazRow): EventStatField[] => {
    const { physical, relay } = resolveAttendance(row);
    return [
      { label: "Forms", value: row.count },
      { label: "On-site — Gents", value: physical.gentsCount },
      { label: "On-site — Ladies", value: physical.ladiesCount },
      { label: "On-site — Children (<10)", value: physical.childrenUnder10 },
      { label: "On-site — Total", value: physical.totalCount },
      { label: "Relay — Gents", value: relay.gentsCount },
      { label: "Relay — Ladies", value: relay.ladiesCount },
      {
        label: "Relay — Total",
        value: relay.totalCount,
        onClick: row.relayRegistrations > 0 ? goToNiyaazWithRelay : undefined,
      },
      { label: "Registered — Total", value: row.totalCount },
      ...buildFinancialFields(row),
    ];
  };

  const buildJamanMobileFields = (row: EventStatsNiyaazRow): EventStatField[] => [
    { label: "Registrations", value: row.count },
    { label: "Gents Count", value: row.gentsCount },
    { label: "Ladies Count", value: row.ladiesCount },
    { label: "Children (<10)", value: row.childrenUnder10 ?? 0 },
    { label: "Total Count", value: row.totalCount ?? 0 },
    ...buildFinancialFields(row),
  ];

  if (isSmall) {
    return (
      <>
        {rows.map((row) => (
          <Box key={row.name} sx={{ mb: 2 }}>
            {isAshara ? <AttendanceSummaryStrip row={row} /> : null}
            <EventStatsMobileGrid
              fields={isAshara ? buildAsharaMobileFields(row) : buildJamanMobileFields(row)}
            />
          </Box>
        ))}
      </>
    );
  }

  if (!isAshara) {
    return (
      <TableContainer
        component={Paper}
        sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}
      >
        <Table size="small" sx={{ minWidth: 1120 }} aria-label="Markaz stats">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={headerCellSx}>
                Registrations
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Gents Count
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Ladies Count
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Children (&lt;10)
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Total Count
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Takhmeen Amount
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Gents Chairs
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Ladies Chairs
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Iftaari
              </TableCell>
              <TableCell
                align="center"
                onClick={goToNiyaazWithZabihats}
                sx={{
                  ...headerCellSx,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                Zabihats
              </TableCell>
              <TableCell
                align="center"
                onClick={goToNiyaazWithPirsa}
                sx={{
                  ...headerCellSx,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                Pirsa
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Payable
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Paid
              </TableCell>
              <TableCell align="center" sx={headerCellSx}>
                Balance
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.name}
                sx={{ "&:last-child td, &:last-child th": { borderBottom: 0 } }}
              >
                <TableCell align="center">{row.count}</TableCell>
                <TableCell align="center">{row.gentsCount}</TableCell>
                <TableCell align="center">{row.ladiesCount}</TableCell>
                <TableCell align="center">{row.childrenUnder10 ?? 0}</TableCell>
                <TableCell align="center">{row.totalCount ?? 0}</TableCell>
                <TableCell align="center">{formatInr(row.takhmeenAmount)}</TableCell>
                <TableCell align="center">{row.gentsChairs ?? 0}</TableCell>
                <TableCell align="center">{row.ladiesChairs ?? 0}</TableCell>
                <TableCell align="center">{formatInr(row.iftaari)}</TableCell>
                <TableCell align="center">{row.zabihat}</TableCell>
                <TableCell align="center">{row.pirsa}</TableCell>
                <TableCell align="center">{formatInr(row.totalPayable)}</TableCell>
                <TableCell align="center">{formatInr(row.paidAmount)}</TableCell>
                <TableCell align="center">{formatInr(row.totalPayable - row.paidAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <AttendanceSummaryStrip key={`${row.name}-summary`} row={row} />
      ))}
      <TableContainer
        component={Paper}
        sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 1400,
            "& tbody .MuiTableRow-root:nth-of-type(even)": { bgcolor: "transparent" },
            "& tbody .MuiTableRow-root:hover": { bgcolor: "transparent" },
          }}
          aria-label="Markaz stats"
        >
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Forms
              </TableCell>
              <TableCell colSpan={4} align="center" sx={groupHeaderSx(theme, "physical")}>
                On-site
              </TableCell>
              <TableCell colSpan={3} align="center" sx={groupHeaderSx(theme, "relay")}>
                Relay (remote)
              </TableCell>
              <TableCell colSpan={4} align="center" sx={groupHeaderSx(theme, "total")}>
                Total
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Niyaaz Amount
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Gents Chairs
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Ladies Chairs
              </TableCell>
              <TableCell
                rowSpan={2}
                align="center"
                onClick={goToNiyaazWithZabihats}
                sx={{
                  ...headerCellSx,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                Zabihats
              </TableCell>
              <TableCell
                rowSpan={2}
                align="center"
                onClick={goToNiyaazWithPirsa}
                sx={{
                  ...headerCellSx,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                Pirsa
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Payable
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Paid
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={headerCellSx}>
                Balance
              </TableCell>
            </TableRow>
            <TableRow>
              <GroupSubHeaderCells group="physical" />
              <GroupSubHeaderCells group="relay" showChildren={false} />
              <GroupSubHeaderCells group="total" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const { physical, relay } = resolveAttendance(row);
              const total: EventStatsAttendanceSlice = {
                gentsCount: row.gentsCount,
                ladiesCount: row.ladiesCount,
                childrenUnder10: row.childrenUnder10,
                totalCount: row.totalCount,
              };

              return (
                <TableRow
                  key={row.name}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": { borderBottom: 0 },
                    "&.MuiTableRow-hover:hover": { backgroundColor: "transparent" },
                  }}
                >
                  <TableCell align="center">{row.count}</TableCell>
                  <AttendanceCells slice={physical} group="physical" />
                  <AttendanceCells slice={relay} showChildren={false} group="relay" />
                  <AttendanceCells slice={total} group="total" />
                  <TableCell align="center">{formatInr(row.takhmeenAmount)}</TableCell>
                  <TableCell align="center">{row.gentsChairs ?? 0}</TableCell>
                  <TableCell align="center">{row.ladiesChairs ?? 0}</TableCell>
                  <TableCell align="center">{row.zabihat}</TableCell>
                  <TableCell align="center">{row.pirsa}</TableCell>
                  <TableCell align="center">{formatInr(row.totalPayable)}</TableCell>
                  <TableCell align="center">{formatInr(row.paidAmount)}</TableCell>
                  <TableCell align="center">
                    {formatInr(row.totalPayable - row.paidAmount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
